import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Avatar, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { DesktopRegular } from "@fluentui/react-icons";
import useBreakpoints from "../../hooks/useBreakpoints";
import { OVERFLOW_TILE_KIND, SCREEN_SHARE_TILE_KIND } from "./participant-layout-participants";
import { ParticipantTileChrome } from "./ParticipantTileChrome";

const DEFAULT_PARTICIPANT_AVATAR_SIZE = 96;
const COMPACT_PARTICIPANT_AVATAR_SIZE = 48;
const PARTICIPANT_AVATAR_SIZE_OPTIONS = [48, 56, 64, 72, 96, 120, 128];
const PARTICIPANT_AVATAR_SIZE_RATIO = 0.5;
const COMPACT_PARTICIPANT_AVATAR_SIZE_RATIO = 0.5;
const MINIMUM_PARTICIPANT_AVATAR_TILE_SIZE = 112;
const DEFAULT_OVERFLOW_PREVIEW_AVATAR_SIZE = 72;
const COMPACT_OVERFLOW_PREVIEW_AVATAR_SIZE = 48;
const DEFAULT_OVERFLOW_MORE_AVATAR_SIZE = 72;
const COMPACT_OVERFLOW_MORE_AVATAR_SIZE = 36;
const DEFAULT_OVERFLOW_AVATAR_OVERLAP = "-22px";
const COMPACT_OVERFLOW_AVATAR_OVERLAP = "-14px";
const PARTICIPANT_SPEAKING_BORDER_COLOR = "var(--color-success)";
const DEFAULT_SCREEN_SHARE_ASPECT_RATIO = 16 / 9;
const MINIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE = 1;
const DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE = 1.1;
const MAXIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE = 4;
const PARTICIPANT_MEDIA_ZOOM_STEP = 0.1;
const FALLBACK_PARTICIPANT_MEDIA_ZOOM_BOUNDS = 300;
const INITIAL_MEDIA_ZOOM_OFFSET = { x: 0, y: 0 };

const useStyles = makeStyles({
  root: {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    border: "1px solid transparent",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--primaryColorBackground)",
    isolation: "isolate"
  },
  speaking: {
    borderTopColor: PARTICIPANT_SPEAKING_BORDER_COLOR,
    borderRightColor: PARTICIPANT_SPEAKING_BORDER_COLOR,
    borderBottomColor: PARTICIPANT_SPEAKING_BORDER_COLOR,
    borderLeftColor: PARTICIPANT_SPEAKING_BORDER_COLOR
  },
  pip: {
    boxShadow: "0 18px 42px rgba(0, 0, 0, 0.28)"
  },
  screenShare: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  pinnedScreenShare: {
    backgroundColor: "transparent"
  },
  pinnedParticipantCamera: {
    backgroundColor: "transparent"
  },
  mediaZoomRoot: {
    overflow: "visible"
  },
  content: {
    display: "flex",
    width: "100%",
    height: "100%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  screenShareContent: {
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center"
  },
  participantVideoContent: {
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "stretch",
    justifyContent: "stretch"
  },
  screenShareFrame: {
    position: "relative",
    display: "flex",
    width: "var(--participant-screen-share-frame-width, 100%)",
    height: "var(--participant-screen-share-frame-height, 100%)",
    maxWidth: "100%",
    maxHeight: "100%",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--secondaryColorBackground)",
    boxShadow: "inset 0 0 0 1px var(--color-border-default)"
  },
  pinnedScreenShareFrame: {
    backgroundColor: "transparent",
    boxShadow: "none"
  },
  mediaZoomFrame: {
    overflow: "visible"
  },
  screenShareVideo: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--primaryColorBackground)"
  },
  stageScreenShareVideo: {
    backgroundColor: "transparent"
  },
  participantVideo: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    backgroundColor: "var(--primaryColorBackground)"
  },
  pinnedParticipantVideo: {
    objectFit: "contain",
    backgroundColor: "transparent"
  },
  mediaZoomTarget: {
    transform:
      "translate3d(var(--participant-media-zoom-x, 0px), var(--participant-media-zoom-y, 0px), 0) scale(var(--participant-media-zoom-scale, 1))",
    transformOrigin: "center",
    transitionProperty: "transform",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease",
    willChange: "transform"
  },
  mediaZoomedTarget: {
    cursor: "grab",
    touchAction: "none"
  },
  mediaZoomPanSurface: {
    cursor: "grab",
    touchAction: "none",
    "&:active": {
      cursor: "grabbing"
    }
  },
  screenShareIcon: {
    width: "64px",
    height: "64px",
    color: "var(--color-text-primary)",
    fill: "var(--color-text-primary)"
  },
  compactScreenShareIcon: {
    width: "48px",
    height: "48px"
  },
  overflowStack: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  overflowAvatarOverlap: {
    marginLeft: DEFAULT_OVERFLOW_AVATAR_OVERLAP
  },
  compactOverflowAvatarOverlap: {
    marginLeft: COMPACT_OVERFLOW_AVATAR_OVERLAP
  }
});

function isCompactParticipantTileBreakpoint(breakpoint) {
  return breakpoint === "xs" || breakpoint === "sm" || breakpoint === "md";
}

function roundScreenShareFrameSize(value) {
  return Math.round(value * 1000) / 1000;
}

export function getContainedScreenShareFrameSize(containerWidth, containerHeight, aspectRatio) {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    !Number.isFinite(aspectRatio) ||
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    aspectRatio <= 0
  ) {
    return null;
  }

  const containerAspectRatio = containerWidth / containerHeight;

  if (containerAspectRatio > aspectRatio) {
    return {
      width: roundScreenShareFrameSize(containerHeight * aspectRatio),
      height: roundScreenShareFrameSize(containerHeight)
    };
  }

  return {
    width: roundScreenShareFrameSize(containerWidth),
    height: roundScreenShareFrameSize(containerWidth / aspectRatio)
  };
}

function getNearestParticipantAvatarSize(targetSize) {
  return PARTICIPANT_AVATAR_SIZE_OPTIONS.reduce((nearestSize, size) => {
    return Math.abs(size - targetSize) <= Math.abs(nearestSize - targetSize) ? size : nearestSize;
  }, PARTICIPANT_AVATAR_SIZE_OPTIONS[0]);
}

export function getParticipantAvatarSizeForBounds(containerWidth, containerHeight, { isCompact = false } = {}) {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    containerWidth <= 0 ||
    containerHeight <= 0
  ) {
    return isCompact ? COMPACT_PARTICIPANT_AVATAR_SIZE : DEFAULT_PARTICIPANT_AVATAR_SIZE;
  }

  if (Math.min(containerWidth, containerHeight) <= MINIMUM_PARTICIPANT_AVATAR_TILE_SIZE) {
    return COMPACT_PARTICIPANT_AVATAR_SIZE;
  }

  const ratio = isCompact ? COMPACT_PARTICIPANT_AVATAR_SIZE_RATIO : PARTICIPANT_AVATAR_SIZE_RATIO;
  const minAvatarSize = PARTICIPANT_AVATAR_SIZE_OPTIONS[0];
  const maxAvatarSize = PARTICIPANT_AVATAR_SIZE_OPTIONS[PARTICIPANT_AVATAR_SIZE_OPTIONS.length - 1];
  const targetSize = Math.min(
    maxAvatarSize,
    Math.max(minAvatarSize, Math.min(containerWidth, containerHeight) * ratio)
  );

  return getNearestParticipantAvatarSize(targetSize);
}

function canAutoPlayParticipantScreenShareVideo() {
  return typeof navigator === "undefined" || !/jsdom/i.test(navigator.userAgent || "");
}

function canZoomParticipantMedia(participant, slot) {
  return Boolean(
    slot.role === "stage" && participant.mediaStream && (participant.isPinned || participant.isRoomSpotlighted)
  );
}

function clampMediaZoomOffset(value, maximum) {
  return Math.min(Math.max(value, -maximum), maximum);
}

function roundMediaZoomScale(scale) {
  return Math.round(scale * 10) / 10;
}

function roundMediaZoomPixelValue(value) {
  return Math.round(value * 1000) / 1000;
}

function ceilMediaZoomScale(scale) {
  return Math.ceil(scale * 10) / 10;
}

function clampMediaZoomScale(scale) {
  return roundMediaZoomScale(
    Math.min(Math.max(scale, MINIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE), MAXIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE)
  );
}

function clampMediaZoomFillScale(scale) {
  return Math.min(
    Math.max(ceilMediaZoomScale(scale), DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE),
    MAXIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE
  );
}

function getMediaZoomPercent(scale) {
  return Math.round(scale * 100);
}

function getUsableElementRect(element) {
  const rect = element?.getBoundingClientRect?.();

  if (!rect || rect.width <= 0 || rect.height <= 0) {
    return null;
  }

  return rect;
}

function getMediaZoomFillScale(viewportElement, mediaElement) {
  const viewportRect = getUsableElementRect(viewportElement);
  const mediaRect = getUsableElementRect(mediaElement);

  if (!viewportRect || !mediaRect) {
    return DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE;
  }

  return clampMediaZoomFillScale(
    Math.max(viewportRect.width / mediaRect.width, viewportRect.height / mediaRect.height)
  );
}

function getMediaZoomOffsetBounds(viewportElement, mediaElement, scale = DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE) {
  const viewportRect = getUsableElementRect(viewportElement);
  const mediaRect = getUsableElementRect(mediaElement) || viewportRect;

  if (!viewportRect || !mediaRect) {
    return {
      x: FALLBACK_PARTICIPANT_MEDIA_ZOOM_BOUNDS * (scale - 1),
      y: FALLBACK_PARTICIPANT_MEDIA_ZOOM_BOUNDS * (scale - 1)
    };
  }

  return {
    x: Math.max(0, (mediaRect.width * scale - viewportRect.width) / 2),
    y: Math.max(0, (mediaRect.height * scale - viewportRect.height) / 2)
  };
}

function getMediaZoomChromeBounds(tileElement, viewportElement) {
  const tileRect = getUsableElementRect(tileElement);
  const viewportRect = getUsableElementRect(viewportElement);

  if (!tileRect || !viewportRect) {
    return null;
  }

  return {
    bottom: roundMediaZoomPixelValue(tileRect.bottom - viewportRect.bottom),
    left: roundMediaZoomPixelValue(viewportRect.left - tileRect.left),
    right: roundMediaZoomPixelValue(tileRect.right - viewportRect.right),
    top: roundMediaZoomPixelValue(viewportRect.top - tileRect.top)
  };
}

function areMediaZoomChromeBoundsEqual(firstBounds, secondBounds) {
  return (
    firstBounds?.top === secondBounds?.top &&
    firstBounds?.right === secondBounds?.right &&
    firstBounds?.bottom === secondBounds?.bottom &&
    firstBounds?.left === secondBounds?.left
  );
}

function getMediaZoomChromeStyle(bounds) {
  if (!bounds) {
    return undefined;
  }

  return {
    "--participant-tile-chrome-bottom": `${bounds.bottom}px`,
    "--participant-tile-chrome-left": `${bounds.left}px`,
    "--participant-tile-chrome-right": `${bounds.right}px`,
    "--participant-tile-chrome-top": `${bounds.top}px`
  };
}

function getMediaZoomOffsetStyle(offset, scale = MINIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE) {
  return {
    "--participant-media-zoom-x": `${offset.x}px`,
    "--participant-media-zoom-y": `${offset.y}px`,
    "--participant-media-zoom-scale": `${scale}`
  };
}

function getTouchCount(event) {
  return event.touches?.length ?? event.targetTouches?.length ?? 0;
}

function preventCancelableTouchDefault(event) {
  if (event.cancelable) {
    event.preventDefault();
  }
}

function ParticipantVideoStream({ className, mediaStream, onVideoDimensionsChange, videoProps }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) {
      return;
    }

    videoElement.srcObject = mediaStream || null;

    if (mediaStream && canAutoPlayParticipantScreenShareVideo() && typeof videoElement.play === "function") {
      const playPromise = videoElement.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    }

    return () => {
      if (videoElement.srcObject === mediaStream) {
        videoElement.srcObject = null;
      }
    };
  }, [mediaStream]);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement || !onVideoDimensionsChange) {
      return undefined;
    }

    const syncVideoDimensions = () => {
      onVideoDimensionsChange(videoElement);
    };

    videoElement.addEventListener("loadedmetadata", syncVideoDimensions);
    videoElement.addEventListener("resize", syncVideoDimensions);
    syncVideoDimensions();

    return () => {
      videoElement.removeEventListener("loadedmetadata", syncVideoDimensions);
      videoElement.removeEventListener("resize", syncVideoDimensions);
    };
  }, [mediaStream, onVideoDimensionsChange]);

  return <video {...videoProps} className={className} muted playsInline ref={videoRef} />;
}

ParticipantVideoStream.propTypes = {
  className: PropTypes.string,
  mediaStream: PropTypes.object,
  onVideoDimensionsChange: PropTypes.func,
  videoProps: PropTypes.object
};

export function ParticipantTile({
  hideFullscreenButton = false,
  hidePinButton = false,
  isFullscreenActive = false,
  participant,
  slot,
  onRequestParticipantFullscreen,
  onRequestParticipantMute,
  onToggleParticipantMuted,
  onToggleParticipantPinned,
  onToggleParticipantRoomSpotlight
}) {
  const styles = useStyles();
  const breakpoint = useBreakpoints();
  const isCompact = isCompactParticipantTileBreakpoint(breakpoint);
  const isScreenShareParticipant = participant.kind === SCREEN_SHARE_TILE_KIND;
  const isPinnedScreenShareParticipant = isScreenShareParticipant && participant.isPinned;
  const usesPinnedCameraPresentation =
    !isScreenShareParticipant && participant.mediaStream && participant.isPinned && slot.role === "stage";
  const canZoomMedia = canZoomParticipantMedia(participant, slot);
  const tileRef = useRef(null);
  const screenShareContentRef = useRef(null);
  const screenShareFrameRef = useRef(null);
  const mediaZoomDragRef = useRef(null);
  const mediaZoomTouchRef = useRef(false);
  const [screenShareAspectRatio, setScreenShareAspectRatio] = useState(DEFAULT_SCREEN_SHARE_ASPECT_RATIO);
  const [isMediaZoomed, setIsMediaZoomed] = useState(false);
  const [mediaZoomChromeBounds, setMediaZoomChromeBounds] = useState(null);
  const [mediaZoomOffset, setMediaZoomOffset] = useState(INITIAL_MEDIA_ZOOM_OFFSET);
  const [mediaZoomScale, setMediaZoomScale] = useState(DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE);
  const [participantAvatarSize, setParticipantAvatarSize] = useState(() =>
    getParticipantAvatarSizeForBounds(0, 0, { isCompact })
  );
  const mediaZoomPercent = getMediaZoomPercent(isMediaZoomed ? mediaZoomScale : MINIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE);
  const canDecreaseMediaZoom = mediaZoomScale > MINIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE;
  const canIncreaseMediaZoom = mediaZoomScale < MAXIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE;
  const tileClassName = mergeClasses(
    styles.root,
    participant.isSpeaking && styles.speaking,
    slot.role === "pip" && styles.pip,
    isScreenShareParticipant && styles.screenShare,
    isPinnedScreenShareParticipant && styles.pinnedScreenShare,
    usesPinnedCameraPresentation && styles.pinnedParticipantCamera,
    canZoomMedia && isMediaZoomed && styles.mediaZoomRoot
  );
  const overflowPreviewAvatarSize = isCompact
    ? COMPACT_OVERFLOW_PREVIEW_AVATAR_SIZE
    : DEFAULT_OVERFLOW_PREVIEW_AVATAR_SIZE;
  const overflowMoreAvatarSize = isCompact ? COMPACT_OVERFLOW_MORE_AVATAR_SIZE : DEFAULT_OVERFLOW_MORE_AVATAR_SIZE;
  const overflowAvatarOverlapClassName = mergeClasses(
    styles.overflowAvatarOverlap,
    isCompact && styles.compactOverflowAvatarOverlap
  );
  const syncScreenShareFrameSize = useCallback(() => {
    const contentElement = screenShareContentRef.current;
    const frameElement = screenShareFrameRef.current;

    if (!contentElement || !frameElement) {
      return;
    }

    const { width, height } = contentElement.getBoundingClientRect();
    const frameSize = getContainedScreenShareFrameSize(width, height, screenShareAspectRatio);

    if (!frameSize) {
      return;
    }

    frameElement.style.setProperty("--participant-screen-share-frame-width", `${frameSize.width}px`);
    frameElement.style.setProperty("--participant-screen-share-frame-height", `${frameSize.height}px`);
  }, [screenShareAspectRatio]);
  const onScreenShareVideoDimensionsChange = useCallback(videoElement => {
    const { videoWidth, videoHeight } = videoElement;

    if (videoWidth > 0 && videoHeight > 0) {
      setScreenShareAspectRatio(videoWidth / videoHeight);
    }
  }, []);
  const requestParticipantMute = useCallback(
    nextParticipant => {
      if (onRequestParticipantMute) {
        onRequestParticipantMute(nextParticipant);
        return;
      }

      onToggleParticipantMuted(nextParticipant.id);
    },
    [onRequestParticipantMute, onToggleParticipantMuted]
  );
  const getParticipantMediaZoomViewportElement = useCallback(() => {
    return (
      tileRef.current?.closest?.('[data-participant-adaptive-grid-main="true"]') ||
      tileRef.current?.closest?.("[data-participant-spotlight-stage]") ||
      screenShareContentRef.current ||
      tileRef.current
    );
  }, []);
  const getParticipantMediaZoomFillScale = useCallback(() => {
    const viewportElement = getParticipantMediaZoomViewportElement();
    const mediaElement = screenShareFrameRef.current || viewportElement;

    return getMediaZoomFillScale(viewportElement, mediaElement);
  }, [getParticipantMediaZoomViewportElement]);
  const getParticipantMediaZoomChromeBounds = useCallback(() => {
    return getMediaZoomChromeBounds(
      screenShareFrameRef.current || tileRef.current,
      getParticipantMediaZoomViewportElement()
    );
  }, [getParticipantMediaZoomViewportElement]);
  const syncParticipantMediaZoomChromeBounds = useCallback(() => {
    if (!canZoomMedia || !isMediaZoomed) {
      setMediaZoomChromeBounds(null);
      return;
    }

    const nextBounds = getParticipantMediaZoomChromeBounds();

    setMediaZoomChromeBounds(currentBounds =>
      areMediaZoomChromeBoundsEqual(currentBounds, nextBounds) ? currentBounds : nextBounds
    );
  }, [canZoomMedia, getParticipantMediaZoomChromeBounds, isMediaZoomed]);
  const resetParticipantMediaZoom = useCallback(() => {
    mediaZoomDragRef.current = null;
    mediaZoomTouchRef.current = false;
    setIsMediaZoomed(false);
    setMediaZoomChromeBounds(null);
    setMediaZoomOffset(INITIAL_MEDIA_ZOOM_OFFSET);
    setMediaZoomScale(DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE);
  }, []);
  const toggleParticipantMediaZoom = useCallback(() => {
    if (!canZoomMedia) {
      return;
    }

    setIsMediaZoomed(currentIsMediaZoomed => {
      if (currentIsMediaZoomed) {
        setMediaZoomChromeBounds(null);
        setMediaZoomOffset(INITIAL_MEDIA_ZOOM_OFFSET);
        setMediaZoomScale(DEFAULT_PARTICIPANT_MEDIA_ZOOM_SCALE);
      } else {
        setMediaZoomChromeBounds(getParticipantMediaZoomChromeBounds());
        setMediaZoomOffset(INITIAL_MEDIA_ZOOM_OFFSET);
        setMediaZoomScale(getParticipantMediaZoomFillScale());
      }

      return !currentIsMediaZoomed;
    });
  }, [canZoomMedia, getParticipantMediaZoomChromeBounds, getParticipantMediaZoomFillScale]);
  const decreaseParticipantMediaZoom = useCallback(() => {
    if (!canZoomMedia || !isMediaZoomed) {
      return;
    }

    setMediaZoomScale(currentScale => clampMediaZoomScale(currentScale - PARTICIPANT_MEDIA_ZOOM_STEP));
  }, [canZoomMedia, isMediaZoomed]);
  const increaseParticipantMediaZoom = useCallback(() => {
    if (!canZoomMedia || !isMediaZoomed) {
      return;
    }

    setMediaZoomScale(currentScale => clampMediaZoomScale(currentScale + PARTICIPANT_MEDIA_ZOOM_STEP));
  }, [canZoomMedia, isMediaZoomed]);
  const onMediaZoomTouchStart = useCallback(
    event => {
      if (!canZoomMedia) {
        return;
      }

      const isPinchGesture = getTouchCount(event) > 1;

      if (!isMediaZoomed && !isPinchGesture) {
        return;
      }

      mediaZoomTouchRef.current = true;
      event.stopPropagation();

      if (isPinchGesture) {
        preventCancelableTouchDefault(event);
        setMediaZoomChromeBounds(getParticipantMediaZoomChromeBounds());
        setMediaZoomScale(getParticipantMediaZoomFillScale());
        setIsMediaZoomed(true);
      }
    },
    [canZoomMedia, getParticipantMediaZoomChromeBounds, getParticipantMediaZoomFillScale, isMediaZoomed]
  );
  const onMediaZoomTouchMove = useCallback(
    event => {
      if (!canZoomMedia) {
        return;
      }

      const isPinchGesture = getTouchCount(event) > 1;

      if (!isMediaZoomed && !mediaZoomTouchRef.current && !isPinchGesture) {
        return;
      }

      mediaZoomTouchRef.current = true;
      event.stopPropagation();
      preventCancelableTouchDefault(event);

      if (isPinchGesture) {
        setMediaZoomChromeBounds(getParticipantMediaZoomChromeBounds());
        setMediaZoomScale(getParticipantMediaZoomFillScale());
        setIsMediaZoomed(true);
      }
    },
    [canZoomMedia, getParticipantMediaZoomChromeBounds, getParticipantMediaZoomFillScale, isMediaZoomed]
  );
  const onMediaZoomTouchEnd = useCallback(
    event => {
      if (!canZoomMedia || (!isMediaZoomed && !mediaZoomTouchRef.current)) {
        return;
      }

      event.stopPropagation();

      if (getTouchCount(event) === 0) {
        mediaZoomTouchRef.current = false;
      }
    },
    [canZoomMedia, isMediaZoomed]
  );
  const onMediaZoomPointerDown = useCallback(
    event => {
      if (!canZoomMedia || !isMediaZoomed || event.button > 0 || event.target.closest?.("button")) {
        return;
      }

      mediaZoomDragRef.current = {
        originX: mediaZoomOffset.x,
        originY: mediaZoomOffset.y,
        pointerX: event.clientX,
        pointerY: event.clientY
      };

      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    },
    [canZoomMedia, isMediaZoomed, mediaZoomOffset.x, mediaZoomOffset.y]
  );
  const onMediaZoomPointerMove = useCallback(
    event => {
      const dragState = mediaZoomDragRef.current;

      if (!dragState || !isMediaZoomed) {
        return;
      }

      const offsetBounds = getMediaZoomOffsetBounds(event.currentTarget, screenShareFrameRef.current, mediaZoomScale);

      setMediaZoomOffset({
        x: clampMediaZoomOffset(dragState.originX + event.clientX - dragState.pointerX, offsetBounds.x),
        y: clampMediaZoomOffset(dragState.originY + event.clientY - dragState.pointerY, offsetBounds.y)
      });
    },
    [isMediaZoomed, mediaZoomScale]
  );
  const onMediaZoomPointerUp = useCallback(event => {
    if (!mediaZoomDragRef.current) {
      return;
    }

    mediaZoomDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);
  const mediaZoomPanHandlers =
    canZoomMedia && isMediaZoomed
      ? {
          onPointerCancel: onMediaZoomPointerUp,
          onPointerDown: onMediaZoomPointerDown,
          onPointerMove: onMediaZoomPointerMove,
          onPointerUp: onMediaZoomPointerUp
        }
      : {};
  const mediaZoomTouchHandlers = canZoomMedia
    ? {
        onTouchCancel: onMediaZoomTouchEnd,
        onTouchEnd: onMediaZoomTouchEnd,
        onTouchMove: onMediaZoomTouchMove,
        onTouchStart: onMediaZoomTouchStart
      }
    : {};
  const mediaZoomTargetClassName = mergeClasses(styles.mediaZoomTarget, isMediaZoomed && styles.mediaZoomedTarget);
  const mediaZoomStyle = canZoomMedia
    ? getMediaZoomOffsetStyle(
        isMediaZoomed ? mediaZoomOffset : INITIAL_MEDIA_ZOOM_OFFSET,
        isMediaZoomed ? mediaZoomScale : MINIMUM_PARTICIPANT_MEDIA_ZOOM_SCALE
      )
    : undefined;
  const tileChromeStyle = canZoomMedia && isMediaZoomed ? getMediaZoomChromeStyle(mediaZoomChromeBounds) : undefined;

  useLayoutEffect(() => {
    if (!isScreenShareParticipant && !usesPinnedCameraPresentation) {
      return undefined;
    }

    const contentElement = screenShareContentRef.current;

    syncScreenShareFrameSize();

    const resizeObserver =
      contentElement && typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncScreenShareFrameSize) : null;

    if (contentElement && resizeObserver) {
      resizeObserver.observe(contentElement);
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", syncScreenShareFrameSize);
    }

    return () => {
      resizeObserver?.disconnect();

      if (typeof window !== "undefined") {
        window.removeEventListener("resize", syncScreenShareFrameSize);
      }
    };
  }, [
    isScreenShareParticipant,
    participant.isPinned,
    slot.role,
    syncScreenShareFrameSize,
    usesPinnedCameraPresentation
  ]);

  useEffect(() => {
    if (
      isScreenShareParticipant ||
      participant.mediaStream ||
      slot.role === "overflow" ||
      participant.kind === OVERFLOW_TILE_KIND
    ) {
      return undefined;
    }

    const tileElement = tileRef.current;

    if (!tileElement) {
      return undefined;
    }

    const syncParticipantAvatarSize = nextRect => {
      setParticipantAvatarSize(
        getParticipantAvatarSizeForBounds(nextRect.width, nextRect.height, {
          isCompact
        })
      );
    };

    syncParticipantAvatarSize(tileElement.getBoundingClientRect());

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(entries => {
            const contentRect = entries[0]?.contentRect;

            syncParticipantAvatarSize(contentRect || tileElement.getBoundingClientRect());
          })
        : null;

    resizeObserver?.observe(tileElement);

    return () => {
      resizeObserver?.disconnect();
    };
  }, [isCompact, isScreenShareParticipant, participant.kind, participant.mediaStream, slot.role]);

  useEffect(() => {
    if (!canZoomMedia) {
      resetParticipantMediaZoom();
    }
  }, [canZoomMedia, participant.id, resetParticipantMediaZoom]);

  useLayoutEffect(() => {
    syncParticipantMediaZoomChromeBounds();

    if (!canZoomMedia || !isMediaZoomed) {
      return undefined;
    }

    const observedElements = [tileRef.current, getParticipantMediaZoomViewportElement()].filter(Boolean);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncParticipantMediaZoomChromeBounds) : null;

    observedElements.forEach(element => resizeObserver?.observe(element));

    if (typeof window !== "undefined") {
      window.addEventListener("resize", syncParticipantMediaZoomChromeBounds);
    }

    return () => {
      resizeObserver?.disconnect();

      if (typeof window !== "undefined") {
        window.removeEventListener("resize", syncParticipantMediaZoomChromeBounds);
      }
    };
  }, [canZoomMedia, getParticipantMediaZoomViewportElement, isMediaZoomed, syncParticipantMediaZoomChromeBounds]);

  useEffect(() => {
    if (!isMediaZoomed) {
      return;
    }

    const viewportElement = getParticipantMediaZoomViewportElement();
    const mediaElement = screenShareFrameRef.current || viewportElement;
    const offsetBounds = getMediaZoomOffsetBounds(viewportElement, mediaElement, mediaZoomScale);

    setMediaZoomOffset(currentOffset => {
      const nextOffset = {
        x: clampMediaZoomOffset(currentOffset.x, offsetBounds.x),
        y: clampMediaZoomOffset(currentOffset.y, offsetBounds.y)
      };

      return nextOffset.x === currentOffset.x && nextOffset.y === currentOffset.y ? currentOffset : nextOffset;
    });
  }, [getParticipantMediaZoomViewportElement, isMediaZoomed, mediaZoomScale]);

  useEffect(() => {
    resetParticipantMediaZoom();
  }, [participant.id, participant.mediaStream, resetParticipantMediaZoom]);

  const tileChrome = (
    <ParticipantTileChrome
      canZoomMedia={canZoomMedia}
      canDecreaseMediaZoom={canDecreaseMediaZoom}
      canIncreaseMediaZoom={canIncreaseMediaZoom}
      chromeStyle={tileChromeStyle}
      hideFullscreenButton={hideFullscreenButton}
      hidePinButton={hidePinButton}
      isFullscreenActive={isFullscreenActive}
      isMediaZoomed={isMediaZoomed}
      mediaZoomPercent={mediaZoomPercent}
      participant={participant}
      onDecreaseParticipantMediaZoom={decreaseParticipantMediaZoom}
      onIncreaseParticipantMediaZoom={increaseParticipantMediaZoom}
      onRequestParticipantFullscreen={onRequestParticipantFullscreen}
      onRequestParticipantMute={requestParticipantMute}
      onToggleParticipantMediaZoom={toggleParticipantMediaZoom}
      onToggleParticipantPinned={onToggleParticipantPinned}
      onToggleParticipantRoomSpotlight={onToggleParticipantRoomSpotlight}
    />
  );
  const screenShareTileContent = participant.mediaStream ? (
    <ParticipantVideoStream
      className={mergeClasses(
        styles.screenShareVideo,
        isPinnedScreenShareParticipant && styles.stageScreenShareVideo,
        canZoomMedia && mediaZoomTargetClassName
      )}
      mediaStream={participant.mediaStream}
      onVideoDimensionsChange={onScreenShareVideoDimensionsChange}
      videoProps={{
        "data-participant-screen-share-video": "",
        "data-participant-media-zoom-target": canZoomMedia ? "screen-share" : undefined,
        "data-participant-media-zoomed": canZoomMedia ? (isMediaZoomed ? "true" : "false") : undefined,
        "data-screen-share-fit": "contain",
        "data-screen-share-presentation": isPinnedScreenShareParticipant ? "pinned" : "contained",
        style: mediaZoomStyle
      }}
    />
  ) : (
    <DesktopRegular
      className={mergeClasses(styles.screenShareIcon, isCompact && styles.compactScreenShareIcon)}
      data-screen-share-icon=""
    />
  );

  if (slot.role === "overflow" || participant.kind === OVERFLOW_TILE_KIND) {
    const previewParticipants = participant.hiddenParticipants.slice(0, 2);

    return (
      <div
        className={tileClassName}
        data-participant-tile=""
        data-participant-id={participant.id}
        data-participant-kind={participant.kind || OVERFLOW_TILE_KIND}
        data-participant-room-spotlighted={participant.isRoomSpotlighted ? "true" : "false"}
        data-participant-speaking={participant.isSpeaking ? "true" : "false"}
        data-slot-role={slot.role}
        data-overflow-count={participant.overflowCount}
      >
        <div className={styles.content}>
          <div className={styles.overflowStack}>
            {previewParticipants.map((previewParticipant, index) => (
              <div
                className={mergeClasses(index > 0 && overflowAvatarOverlapClassName)}
                data-overflow-avatar-size={overflowPreviewAvatarSize}
                data-overflow-participant-avatar=""
                key={previewParticipant.id}
              >
                <Avatar
                  name={previewParticipant.displayName}
                  initials={previewParticipant.initials}
                  color={previewParticipant.isSelf ? "brand" : "colorful"}
                  size={overflowPreviewAvatarSize}
                />
              </div>
            ))}
            {participant.overflowCount > 0 && (
              <div
                className={mergeClasses(previewParticipants.length > 0 && overflowAvatarOverlapClassName)}
                data-overflow-avatar-size={overflowMoreAvatarSize}
                data-overflow-more-avatar=""
              >
                <Avatar initials={`+${participant.overflowCount}`} size={overflowMoreAvatarSize} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={tileClassName}
      data-participant-tile=""
      data-participant-id={participant.id}
      data-participant-kind={participant.kind || "participant"}
      data-participant-role={participant.role || ""}
      data-participant-room-spotlighted={participant.isRoomSpotlighted ? "true" : "false"}
      data-participant-speaking={participant.isSpeaking ? "true" : "false"}
      data-participant-camera-background={
        !isScreenShareParticipant && participant.mediaStream
          ? usesPinnedCameraPresentation
            ? "hidden"
            : "visible"
          : undefined
      }
      data-participant-media-zoomed={canZoomMedia ? (isMediaZoomed ? "true" : "false") : undefined}
      data-participant-media-zoom-percent={canZoomMedia ? `${mediaZoomPercent}` : undefined}
      data-slot-role={slot.role}
      data-screen-share-background={
        isScreenShareParticipant ? (isPinnedScreenShareParticipant ? "hidden" : "visible") : undefined
      }
      ref={tileRef}
    >
      <div
        className={mergeClasses(styles.content, canZoomMedia && isMediaZoomed && styles.mediaZoomPanSurface)}
        {...mediaZoomPanHandlers}
        {...mediaZoomTouchHandlers}
      >
        {isScreenShareParticipant ? (
          <div className={styles.screenShareContent} data-screen-share-content="" ref={screenShareContentRef}>
            <div
              className={mergeClasses(
                styles.screenShareFrame,
                isPinnedScreenShareParticipant && styles.pinnedScreenShareFrame,
                canZoomMedia && isMediaZoomed && styles.mediaZoomFrame
              )}
              data-screen-share-frame=""
              data-screen-share-frame-background={isPinnedScreenShareParticipant ? "hidden" : "visible"}
              data-screen-share-frame-presentation={isPinnedScreenShareParticipant ? "pinned" : "contained"}
              data-participant-media-zoom-frame={canZoomMedia ? (isMediaZoomed ? "expanded" : "contained") : undefined}
              ref={screenShareFrameRef}
            >
              {screenShareTileContent}
              {isPinnedScreenShareParticipant && tileChrome}
            </div>
          </div>
        ) : participant.mediaStream && usesPinnedCameraPresentation ? (
          <div className={styles.screenShareContent} data-participant-camera-content="" ref={screenShareContentRef}>
            <div
              className={mergeClasses(
                styles.screenShareFrame,
                styles.pinnedScreenShareFrame,
                canZoomMedia && isMediaZoomed && styles.mediaZoomFrame
              )}
              data-participant-camera-frame=""
              data-participant-camera-frame-background="hidden"
              data-participant-media-zoom-frame={canZoomMedia ? (isMediaZoomed ? "expanded" : "contained") : undefined}
              ref={screenShareFrameRef}
            >
              <ParticipantVideoStream
                className={mergeClasses(
                  styles.participantVideo,
                  styles.pinnedParticipantVideo,
                  mediaZoomTargetClassName
                )}
                mediaStream={participant.mediaStream}
                onVideoDimensionsChange={onScreenShareVideoDimensionsChange}
                videoProps={{
                  "data-participant-camera-video": "",
                  "data-participant-camera-background": "hidden",
                  "data-participant-camera-presentation": "pinned",
                  "data-participant-media-zoom-target": canZoomMedia ? "camera" : undefined,
                  "data-participant-media-zoomed": canZoomMedia ? (isMediaZoomed ? "true" : "false") : undefined,
                  style: mediaZoomStyle
                }}
              />
              {tileChrome}
            </div>
          </div>
        ) : participant.mediaStream ? (
          <div className={styles.participantVideoContent} data-participant-video-content="">
            <ParticipantVideoStream
              className={mergeClasses(styles.participantVideo, canZoomMedia && mediaZoomTargetClassName)}
              mediaStream={participant.mediaStream}
              videoProps={{
                "data-participant-camera-video": "",
                "data-participant-camera-background": "visible",
                "data-participant-camera-presentation": "cover",
                "data-participant-media-zoom-target": canZoomMedia ? "camera" : undefined,
                "data-participant-media-zoomed": canZoomMedia ? (isMediaZoomed ? "true" : "false") : undefined,
                style: mediaZoomStyle
              }}
            />
          </div>
        ) : (
          <div data-participant-avatar="" data-participant-avatar-size={participantAvatarSize}>
            <Avatar
              name={participant.displayName}
              initials={participant.initials}
              color={participant.isSelf ? "brand" : "colorful"}
              size={participantAvatarSize}
            />
          </div>
        )}
      </div>
      {!isPinnedScreenShareParticipant && !usesPinnedCameraPresentation && tileChrome}
    </div>
  );
}

ParticipantTile.propTypes = {
  hideFullscreenButton: PropTypes.bool,
  hidePinButton: PropTypes.bool,
  isFullscreenActive: PropTypes.bool,
  participant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.string,
    displayName: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
    isMuted: PropTypes.bool,
    isPinned: PropTypes.bool,
    isSelf: PropTypes.bool,
    isSpeaking: PropTypes.bool,
    isHandRaised: PropTypes.bool,
    role: PropTypes.string,
    canToggleMuted: PropTypes.bool,
    canToggleRoomSpotlight: PropTypes.bool,
    mediaStream: PropTypes.object,
    isRoomSpotlighted: PropTypes.bool,
    roomSpotlightActive: PropTypes.bool,
    hiddenParticipants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        initials: PropTypes.string.isRequired,
        isSelf: PropTypes.bool
      })
    ),
    overflowCount: PropTypes.number
  }).isRequired,
  slot: PropTypes.shape({
    role: PropTypes.string.isRequired
  }).isRequired,
  onToggleParticipantMuted: PropTypes.func.isRequired,
  onRequestParticipantFullscreen: PropTypes.func,
  onRequestParticipantMute: PropTypes.func,
  onToggleParticipantPinned: PropTypes.func.isRequired,
  onToggleParticipantRoomSpotlight: PropTypes.func
};
